import React, { useState, useRef, useEffect } from 'react';
import css from './styles.module.scss';

function CustomSelect(props) {
  const {
    className = '',
    id,
    label,
    icon,
    prefixText,
    placeholder,
    value,
    onChange,
    displayError = false,
    dataErrors,
    children,
    renderCustomOption,
    onOpen,
    ...other
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [focusedOptionIndex, setFocusedOptionIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const optionsRef = useRef([]);
  const comboboxRef = useRef(null);

  const options = React.Children.toArray(children).map((child) => ({
    value: child.props.value,
    label: child.props.children,
    className: child.props.className || '',
    disabled: child.props.disabled || false,
  }));

  // Determinar o valor exibido
  const selectedOption = value && value !== 'placeholder'
    ? options.find((option) => option.value === value)?.label || placeholder
    : placeholder;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setFocusedOptionIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const handleOptionClick = (option, index) => {
    if (option.disabled) {
      return;
    }
    setIsOpen(false);
    setFocusedOptionIndex(-1);
    if (onChange) {
      onChange({ target: { value: option.value } });
    }
    comboboxRef.current?.focus();
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setFocusedOptionIndex(-1);
      if (onOpen) {
        onOpen();
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (isOpen && focusedOptionIndex >= 0) {
        if (!options[focusedOptionIndex].disabled) {
          handleOptionClick(options[focusedOptionIndex], focusedOptionIndex);
        }
      } else if (!isOpen) {
        toggleDropdown();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (isOpen) {
        const newIndex = Math.min(focusedOptionIndex + 1, options.length - 1);
        setFocusedOptionIndex(newIndex);
      } else {
        setIsOpen(true);
        setFocusedOptionIndex(0);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (isOpen) {
        const newIndex = Math.max(focusedOptionIndex - 1, 0);
        setFocusedOptionIndex(newIndex);
      } else {
        setIsOpen(true);
        setFocusedOptionIndex(options.length - 1);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      setFocusedOptionIndex(-1);
    }
  };

  const handleOptionKeyDown = (e, option, index) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!option.disabled) {
        handleOptionClick(option, index);
      }
    }
  };

  return (
    <div
      className={`${css['atom__custom-select-container']} ${className}`}
      ref={dropdownRef}
      data-test-id="custom-select-container"
      {...other}
    >
      {label && (
        <label id={`${id}-label`} htmlFor={id} className={css['custom-select-label']}>
          {label}
        </label>
      )}
      <div
        className={css['custom-select']}
        ref={comboboxRef}
        id={id}
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={`${id}-listbox`}
        aria-labelledby={label ? `${id}-label` : undefined}
        aria-label={label ? undefined : placeholder || 'Select an option'}
        aria-activedescendant={isOpen && focusedOptionIndex >= 0 ? `${id}-option-${focusedOptionIndex}` : undefined}
        tabIndex={0}
        data-test-id="custom-select-combobox"
      >
        <div className={css['custom-select-container']}>
          {icon && (
            <span className={css['custom-select-icon']} aria-hidden="true">
              {icon}
            </span>
          )}
          <div className={css['custom-select-prefix-value']}>
            {prefixText && <span className={css['custom-select-prefix']}>{prefixText}</span>}
            <span className={css['custom-select-value']}>{selectedOption}</span>
          </div>
        </div>
        <span className={css['custom-select-arrow']} />
      </div>
      {isOpen && (
        <ul id={`${id}-listbox`} className={css['custom-select-options']} role="listbox">
          {options.map((option, index) => {
            const optionClass = `${css['custom-select-option']} ${option.className} ${option.value === value ? css['selected'] : ''
              } ${focusedOptionIndex === index ? css['focused'] : ''}`.trim();
            return renderCustomOption ? (
              renderCustomOption({
                option,
                index,
                optionClass,
                handleOptionClick,
                handleOptionKeyDown,
                value,
              })
            ) : (
              <li
                key={index}
                id={`${id}-option-${index}`}
                className={optionClass}
                onClick={() => handleOptionClick(option, index)}
                onKeyDown={(e) => handleOptionKeyDown(e, option, index)}
                role="option"
                aria-selected={option.value === value}
                aria-disabled={option.disabled || undefined}
                tabIndex={-1}
                ref={(el) => (optionsRef.current[index] = el)}
                data-test-id={`custom-select-option-${index}`}
              >
                {option.label}
              </li>
            );
          })}
        </ul>
      )}
      {displayError && dataErrors?.type && dataErrors.message && (
        <span
          id={`${id}-error`}
          className={css['custom-select-error']}
          data-test-id="custom-select-error"
        >
          {dataErrors.message}
        </span>
      )}
    </div>
  );
}

export default CustomSelect;
